import { cartQuantities, evaluateSelection } from './nw-finish-purchase.js';

export function mysteryCandidates(config, finishes, inCart = new Map(), availableOnly = true) {
  if (![1, 2, 3].includes(finishes.length)) return [];
  const keys = Object.keys(config.products);
  const allied = { Fire: 'Air', Air: 'Fire', Water: 'Earth', Earth: 'Water' };
  const candidates = [];
  function visit(chosen) {
    if (chosen.length === finishes.length) {
      const result = evaluateSelection(config, chosen.map((slug, index) => ({ slug, finish: finishes[index] })), inCart);
      if (result.total !== null && (result.valid || !availableOnly)) candidates.push(result);
      return;
    }
    for (const key of keys) {
      if (chosen.includes(key)) continue;
      if (chosen.length && config.elements[key] !== allied[config.elements[chosen[0]]]) continue;
      visit([...chosen, key]);
    }
  }
  visit([]);
  return candidates;
}

export function randomMystery(candidates, random = crypto) {
  if (!candidates.length) return null;
  const limit = Math.floor(4294967296 / candidates.length) * candidates.length;
  const value = new Uint32Array(1);
  do { random.getRandomValues(value); } while (value[0] >= limit);
  return candidates[value[0] % candidates.length];
}

export function initMysteryFinishes(funnel) {
  if (funnel.dataset.finishReady) return;
  const config = JSON.parse(funnel.querySelector('[data-mystery-config]').textContent);
  const forms = [...funnel.querySelectorAll('.nf-mystery-form')];
  const status = funnel.querySelector('[data-mystery-status]');
  const cartLink = funnel.querySelector('[data-mystery-cart]');
  let inCart = cartQuantities(config.cartItems), busy = false, needsCartReview = false;
  const finishesFor = form => [...form.querySelectorAll('[data-mystery-finish]')].map(select => select.value);
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: config.currency || 'USD' }).format(cents / 100);
  const range = numbers => numbers.length ? money(Math.min(...numbers)) + (Math.min(...numbers) === Math.max(...numbers) ? '' : ' – ' + money(Math.max(...numbers))) : '—';
  function refresh() {
    forms.forEach(form => {
      const finishes = finishesFor(form), possible = mysteryCandidates(config, finishes, inCart), priced = possible.length ? possible : mysteryCandidates(config, finishes, inCart, false);
      const card = form.closest('.nf-offer-card'), button = form.querySelector('button');
      button.disabled = busy || needsCartReview || !possible.length;
      button.setAttribute('aria-busy', String(busy));
      form.querySelector('fieldset').disabled = busy;
      card.querySelector('[data-offer-total]').textContent = range(priced.map(result => result.total));
      const reference = card.querySelector('[data-offer-reference]');
      if (reference) reference.textContent = range(priced.map(result => result.gross));
      const savings = card.querySelector('[data-offer-saving]');
      if (savings) savings.textContent = config.saving.replace('__amount__', range(priced.map(result => result.gross - result.total))).replace('__percent__', finishes.length === 2 ? '20' : '30');
      card.querySelector('[data-offer-unit]').textContent = config.perMedallion.replace('__price__', range(priced.map(result => Math.round(result.total / finishes.length))));
      form.querySelector('[data-mystery-availability]').textContent = possible.length ? '' : config.unavailable;
    });
  }
  forms.forEach(form => {
    form.addEventListener('change', () => {
      if (busy) return;
      if (!needsCartReview) { status.textContent = ''; cartLink.hidden = true; }
      refresh();
    });
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (busy || needsCartReview) return;
      const finishes = finishesFor(form);
      if (!mysteryCandidates(config, finishes, inCart).length) { status.textContent = config.unavailable; return; }
      busy = true; status.textContent = config.pending; cartLink.hidden = true; refresh();
      let attempted = false, added = false;
      try {
        const cartResponse = await fetch(config.root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('Cart could not be checked');
        const currentCart = await cartResponse.json();
        if (!Array.isArray(currentCart.items)) throw new Error('Cart data is missing');
        inCart = cartQuantities(currentCart.items);
        const selected = randomMystery(mysteryCandidates(config, finishes, inCart));
        if (!selected) { status.textContent = config.unavailable; cartLink.hidden = false; return; }
        const group = crypto.randomUUID();
        const items = selected.items.map(item => ({ ...item, properties: { _nw_mystery: 'true', _nw_mystery_group: group } }));
        const drawer = document.getElementById('cart-drawer'), sectionId = drawer?.closest('.shopify-section')?.id.replace('shopify-section-', '');
        const payload = { items };
        if (sectionId) { payload.sections = [sectionId]; payload.sections_url = location.pathname + location.search; }
        attempted = true;
        const response = await fetch(config.root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
        if (!response.ok) {
          needsCartReview = true; status.textContent = config.error; cartLink.hidden = false;
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          return;
        }
        added = true;
        items.forEach(item => inCart.set(item.id, (inCart.get(item.id) || 0) + item.quantity));
        const data = await response.json();
        if (!drawer || !sectionId || !data.sections?.[sectionId] || typeof drawer.show !== 'function') { location.assign(config.cartUrl); return; }
        const fresh = new DOMParser().parseFromString(data.sections[sectionId], 'text/html').getElementById('cart-drawer');
        if (!fresh) { location.assign(config.cartUrl); return; }
        drawer.replaceChildren(...Array.from(fresh.childNodes));
        drawer.classList.toggle('drawer--center-body', fresh.classList.contains('drawer--center-body'));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        await drawer.show(); status.textContent = config.success; cartLink.hidden = false;
      } catch (error) {
        if (added) location.assign(config.cartUrl);
        else { needsCartReview = attempted; status.textContent = attempted ? config.uncertain : config.checkError; cartLink.hidden = !attempted; }
      } finally { busy = false; refresh(); }
    });
  });
  funnel.dataset.finishReady = 'true';
  refresh();
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('nw-fate-funnel').forEach(initMysteryFinishes);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('nw-fate-funnel').forEach(initMysteryFinishes));
}
